import { useBranding } from '../../context/BrandingContext';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, Award, PlayCircle, BookOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

// Universidade Elosaúde — catálogo do aluno (fatia 1 do port React).
// Player do curso, prova e certificado entram nas próximas fatias.

const COR = { primaria: 'var(--brand, #20a490)', sucesso: '#27ae60', alerta: '#f39c12', info: '#3498db' };

const AcademiaAluno = () => {
  const navigate = useNavigate();
  const [categorias, setCategorias] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [catAtiva, setCatAtiva] = useState(null); // null = todas
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [catRes, cursoRes] = await Promise.all([
          api.get('/academia/categorias'),
          api.get('/academia/cursos'),
        ]);
        setCategorias(catRes.data || []);
        setCursos(cursoRes.data || []);
      } catch (e) {
        const detail = e.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Erro ao carregar a Universidade');
      }
      setLoading(false);
    })();
  }, []);

  const cursosFiltrados = catAtiva
    ? cursos.filter((c) => c.categoria_id === catAtiva)
    : cursos;

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.35rem' }}>
        <GraduationCap size={26} style={{ color: COR.primaria }} />
        <h2 style={{ margin: 0 }}>Universidade {branding.nome_operadora}</h2>
      </div>
      <p style={{ color: 'var(--gray-500)', marginBottom: '1.25rem' }}>
        Cursos internos para o seu desenvolvimento. Assista, faça a prova e conquiste o certificado.
      </p>

      {/* Filtro por categoria */}
      {categorias.length > 0 && (
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
          <Chip ativo={catAtiva === null} onClick={() => setCatAtiva(null)} label="Todas" />
          {categorias.map((cat) => (
            <Chip
              key={cat.id}
              ativo={catAtiva === cat.id}
              onClick={() => setCatAtiva(cat.id)}
              label={cat.nome}
            />
          ))}
        </div>
      )}

      {/* Grade de cursos */}
      {cursosFiltrados.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--gray-500)', padding: '3rem 1rem' }}>
          <BookOpen size={40} style={{ color: 'var(--gray-300)', marginBottom: '0.5rem' }} />
          <p>Nenhum curso publicado {catAtiva ? 'nesta categoria' : 'ainda'}.</p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: '1.1rem',
        }}>
          {cursosFiltrados.map((c) => (
            <CursoCard key={c.id} curso={c} onClick={() => navigate(`/academia/curso/${c.id}`)} />
          ))}
        </div>
      )}
    </div>
  );
};

const Chip = ({ ativo, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '0.35rem 0.85rem', borderRadius: '20px', fontSize: '0.82rem',
      fontWeight: ativo ? 600 : 400, cursor: 'pointer', transition: 'all 0.15s',
      border: ativo ? `2px solid ${COR.primaria}` : '2px solid #e2e8f0',
      background: ativo ? '#e7f7f3' : '#fff',
      color: ativo ? COR.primaria : '#475569',
    }}
  >
    {label}
  </button>
);

const CursoCard = ({ curso, onClick }) => {
  const capa = curso.capa_thumb || curso.capa_url;
  const total = curso.total_aulas || 0;
  const feitas = curso.aulas_concluidas || 0;
  const pct = total ? Math.round((feitas / total) * 100) : 0;

  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '12px',
        overflow: 'hidden', display: 'flex', flexDirection: 'column', cursor: 'pointer',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'transform 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.10)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; }}
    >
      {/* Capa */}
      <div style={{
        position: 'relative', height: '140px',
        background: capa
          ? `center/cover no-repeat url("${capa}")`
          : 'linear-gradient(135deg, var(--brand, #20a490), #27ae60)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {!capa && <PlayCircle size={42} style={{ color: 'rgba(255,255,255,0.9)' }} />}
        {curso.tem_certificado && (
          <span style={{
            position: 'absolute', top: '8px', right: '8px',
            display: 'flex', alignItems: 'center', gap: '0.25rem',
            background: 'rgba(255,255,255,0.95)', color: COR.sucesso,
            fontSize: '0.7rem', fontWeight: 700, padding: '0.2rem 0.5rem', borderRadius: '12px',
          }}>
            <Award size={13} /> Concluído
          </span>
        )}
      </div>

      {/* Corpo */}
      <div style={{ padding: '0.85rem 0.9rem', display: 'flex', flexDirection: 'column', flex: 1 }}>
        {curso.categoria_nome && (
          <span style={{
            fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
            color: COR.primaria, marginBottom: '0.3rem',
          }}>
            {curso.categoria_nome}
          </span>
        )}
        <strong style={{ fontSize: '0.95rem', color: '#16323a', lineHeight: 1.25 }}>
          {curso.titulo}
        </strong>
        {curso.descricao && (
          <p style={{
            fontSize: '0.8rem', color: 'var(--gray-500)', margin: '0.35rem 0 0',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {curso.descricao}
          </p>
        )}

        {/* Rodapé: progresso */}
        <div style={{ marginTop: 'auto', paddingTop: '0.75rem' }}>
          <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              width: `${pct}%`, height: '100%', borderRadius: '4px',
              background: `linear-gradient(90deg, ${COR.primaria}, ${COR.sucesso})`,
              transition: 'width 0.4s',
            }} />
          </div>
          <div style={{ fontSize: '0.74rem', color: 'var(--gray-500)', marginTop: '0.3rem' }}>
            {feitas}/{total} aulas {pct === 100 && total > 0 ? '· completo' : ''}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcademiaAluno;
